import PropTypes from 'prop-types';
import React from 'react';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { Icon, orange, tokens, useIsMobile } from 'components/v3';
import { publicFormUrl } from '../shared/publicFormUrl';

// The design's ShareBar (public-form plan §4, P5): the form's public web
// link with Copy and Open form, above the stats on the Submissions page.
// The URL shows a muted scheme and a bold host and path; it truncates on
// desktop and wraps on phones, where the buttons drop below it. A form
// that isn't Live (or is archived) gets a hint, because people who open
// the link see "This form isn't available". No link (Kart Pass without an
// override) means no bar.
// Optional (w9-plan §6.3 item 3): `url` replaces the form's public link
// (the W-9's vanity URL), `label` the caption, `extraActions` adds buttons
// after Open form (the QR code button). Defaults: today's bar.

export const COPY_ERROR =
  "Couldn't copy the link. Select it and copy it instead.";
export const NOT_LIVE_HINT =
  "People see “This form isn't available” until you go Live.";

// "https://www.pitpay.com/f/abc" → ["https://", "www.pitpay.com/f/abc"]
export const splitUrl = (url) => {
  const at = url.indexOf('://');
  return at < 0 ? ['', url] : [url.slice(0, at + 3), url.slice(at + 3)];
};

// Resolves when the text is on the clipboard; rejects when there is no
// clipboard (older browsers, http pages) or the browser refuses.
const writeClipboard = (text) =>
  Promise.resolve().then(() => {
    const clipboard =
      typeof navigator === 'undefined' ? null : navigator.clipboard;
    if (!clipboard || typeof clipboard.writeText !== 'function') {
      throw new Error('Clipboard unavailable');
    }
    return clipboard.writeText(text);
  });

export const copyLink = (url, onToast) =>
  writeClipboard(url).then(
    () => onToast({ type: 'success', title: 'Link copied' }),
    () => onToast({ type: 'error', title: COPY_ERROR })
  );

const isLiveForm = (form) => form.status === 'live' && !form.archived_at;

const barStyles = (t, isMobile) => ({
  bar: {
    display: 'flex',
    flexWrap: isMobile ? 'wrap' : 'nowrap',
    alignItems: 'center',
    gap: isMobile ? 12 : 14,
    padding: isMobile ? 14 : '14px 18px',
    background: `linear-gradient(90deg, ${orange(0.1)}, ${orange(0.02)})`,
    border: '1px solid ' + orange(0.28),
    borderRadius: 14,
  },
  tile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: 10,
    background: orange(0.16),
    color: t.primary,
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: t.primary,
  },
  url: {
    marginTop: 3,
    fontSize: 16,
    color: t.sub,
    ...(isMobile
      ? { overflowWrap: 'anywhere' }
      : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
  },
  hint: { marginTop: 4, fontSize: 12.5, color: t.sub },
  actions: {
    display: 'flex',
    gap: 10,
    flex: isMobile ? '1 1 100%' : '0 0 auto',
  },
  button: {
    width: 'auto',
    flex: isMobile ? '1 1 0' : '0 0 auto',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
  },
});

export const DEFAULT_LABEL = 'Public form link';

const PublicLinkBar = ({
  form,
  onToast,
  url: urlProp,
  label,
  extraActions,
}) => {
  const t = tokens(useTheme());
  const isMobile = useIsMobile();
  const url = urlProp || publicFormUrl(form && form.public_id);
  if (!url) return null;

  const s = barStyles(t, isMobile);
  const [scheme, rest] = splitUrl(url);
  return (
    <section aria-label={label} style={s.bar} data-tour="submissions-share">
      <div aria-hidden="true" style={s.tile}>
        <Icon name="link" size={17} color="currentColor" />
      </div>
      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <div style={s.label}>{label}</div>
        <div style={s.url} title={url}>
          {scheme}
          <strong style={{ color: t.text, fontWeight: 600 }}>{rest}</strong>
        </div>
        {isLiveForm(form) ? null : <div style={s.hint}>{NOT_LIVE_HINT}</div>}
      </div>
      <div style={s.actions}>
        <ButtonV2
          onClick={() => copyLink(url, onToast)}
          icon={<Icon name="copy" size={15} color="currentColor" />}
          style={s.button}
          data-help="copy-link"
        >
          Copy
        </ButtonV2>
        <ButtonV2
          primary
          as="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          icon={<Icon name="external-link" size={15} color="currentColor" />}
          style={s.button}
          data-help="open-form"
        >
          Open form
        </ButtonV2>
        {extraActions}
      </div>
    </section>
  );
};

PublicLinkBar.propTypes = {
  form: PropTypes.shape({
    public_id: PropTypes.string,
    status: PropTypes.string,
    archived_at: PropTypes.string,
  }),
  onToast: PropTypes.func.isRequired,
  url: PropTypes.string,
  label: PropTypes.string,
  extraActions: PropTypes.node,
};

PublicLinkBar.defaultProps = {
  form: null,
  url: undefined,
  label: DEFAULT_LABEL,
  extraActions: null,
};

export default PublicLinkBar;
